/**
 * Roadmap #146 Alt-PR E — count `console.error`, `window.error`, and
 * `unhandledrejection` events while the user QAs a preview.
 *
 * The counter resets on hard reload (no persistence between sessions).
 * Resets on demand via `reset()`.
 */
import { useEffect, useState, useCallback } from 'react';

interface ErrorRecord {
  ts: number;
  message: string;
  source: 'console.error' | 'window.error' | 'unhandledrejection';
}

const MAX_RECORDS = 50;

export function useConsoleErrors(): {
  count: number;
  recent: ErrorRecord[];
  reset: () => void;
} {
  const [records, setRecords] = useState<ErrorRecord[]>([]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const push = (rec: ErrorRecord) =>
      setRecords((prev) => {
        const next = [...prev, rec];
        return next.length > MAX_RECORDS ? next.slice(next.length - MAX_RECORDS) : next;
      });

    const origError = console.error;
    console.error = (...args: unknown[]) => {
      push({
        ts: Date.now(),
        source: 'console.error',
        message: args.map((a) => stringify(a)).join(' ').slice(0, 500),
      });
      origError.apply(console, args as []);
    };

    const onError = (ev: ErrorEvent) => {
      push({
        ts: Date.now(),
        source: 'window.error',
        message: (ev.message || String(ev.error)).slice(0, 500),
      });
    };
    const onRejection = (ev: PromiseRejectionEvent) => {
      const reason = ev.reason instanceof Error ? ev.reason.message : String(ev.reason);
      push({
        ts: Date.now(),
        source: 'unhandledrejection',
        message: reason.slice(0, 500),
      });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      console.error = origError;
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  const reset = useCallback(() => setRecords([]), []);

  return {
    count: records.length,
    recent: records,
    reset,
  };
}

function stringify(v: unknown): string {
  if (v == null) return String(v);
  if (typeof v === 'string') return v;
  if (v instanceof Error) return v.message;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
